import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import leaveRequestService from '../services/leaveRequestService';
import staffAttendanceService from '../services/staffAttendanceService';
import StaffChat from '../components/communication/StaffChat';
import {
  Users, UserCheck, CalendarBlank, Wallet, User, Envelope, Briefcase, ChatCircle
} from '@phosphor-icons/react';

const todayISO = () => new Date().toISOString().split('T')[0];

function HRPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pendingLeave, setPendingLeave] = useState(0);
  const [roster, setRoster] = useState({ total_staff: 0, recorded: 0 });
  const [tab, setTab] = useState('dashboard');

  useEffect(() => {
    if (user && user.role !== 'hr') {
      toast.error('You do not have access to this page');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.school_id) return;

    Promise.all([
      leaveRequestService.list({ status: 'pending' }),
      staffAttendanceService.getRoster(todayISO()),
    ])
      .then(([leaveData, rosterData]) => {
        setPendingLeave(Array.isArray(leaveData) ? leaveData.length : (leaveData?.items?.length || 0));
        setRoster({
          total_staff: rosterData?.total_staff || 0,
          recorded: rosterData?.recorded || 0,
        });
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load HR overview');
      })
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return (
      <Layout>
        <div className="page">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading HR portal...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>HR Portal</h1>
            <p>Staff attendance, leave approvals, and payroll at a glance</p>
          </div>
          <div className="header-actions">
            <div className="user-info">
              <User size={20} />
              <span>{user?.first_name} {user?.last_name}</span>
            </div>
          </div>
        </div>

        <div className="tabs-container">
          <button className={`tab-btn ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => setTab('dashboard')}>
            <Briefcase size={18} /> Dashboard
          </button>
          <button className={`tab-btn ${tab === 'messages' ? 'active' : ''}`} onClick={() => setTab('messages')}>
            <ChatCircle size={18} /> Messages
          </button>
        </div>

        {tab === 'messages' ? <StaffChat /> : (
        <>
        <div className="dashboard-grid">
          <div className="dashboard-card">
            <h3>My Profile</h3>
            <div className="profile-info">
              <div className="profile-item">
                <div className="profile-icon"><User size={20} /></div>
                <div className="profile-content">
                  <div className="profile-label">Name</div>
                  <div className="profile-value">{user?.first_name} {user?.last_name}</div>
                </div>
              </div>
              <div className="profile-item">
                <div className="profile-icon"><Envelope size={20} /></div>
                <div className="profile-content">
                  <div className="profile-label">Email</div>
                  <div className="profile-value">{user?.email}</div>
                </div>
              </div>
              <div className="profile-item">
                <div className="profile-icon"><Briefcase size={20} /></div>
                <div className="profile-content">
                  <div className="profile-label">Role</div>
                  <div className="profile-value">HR</div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Today's Attendance</h3>
            <div className="stats-grid">
              <div className="stat">
                <div className="stat-value">{roster.total_staff}</div>
                <div className="stat-label">Active Staff</div>
              </div>
              <div className="stat">
                <div className="stat-value">{roster.recorded}</div>
                <div className="stat-label">Recorded</div>
              </div>
              <div className="stat">
                <div className="stat-value">{Math.max(roster.total_staff - roster.recorded, 0)}</div>
                <div className="stat-label">Unrecorded</div>
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Leave Requests</h3>
            <div className="stats-grid">
              <div className="stat">
                <div className="stat-value">{pendingLeave}</div>
                <div className="stat-label">Pending</div>
              </div>
            </div>
            {pendingLeave === 0 && <p className="text-muted">No pending leave requests</p>}
          </div>
        </div>

        <div className="quick-actions">
          <h3>Quick Actions</h3>
          <div className="actions-grid">
            <button className="action-btn" onClick={() => navigate('/staff-attendance')}>
              <UserCheck size={24} />
              <span>Staff Attendance</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/leave-requests')}>
              <CalendarBlank size={24} />
              <span>Leave Requests</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/payroll')}>
              <Wallet size={24} />
              <span>Payroll</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/staff')}>
              <Users size={24} />
              <span>Staff Directory</span>
            </button>
          </div>
        </div>
        </>
        )}
      </div>
    </Layout>
  );
}

export default HRPortalPage;
